import { forwardRef } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Icon, cn } from "components/lib";

const Checkbox = forwardRef(
  (
    // Temporary Case for Disabled Scopes
    {
      className,
      name,
      onChange,
      checked,
      defaultChecked,
      required,
      label,
      disabled = false,
      tooltip = null,
    },
    ref
  ) => {
    return (
      <div className="flex mb-2 relative group">
        <CheckboxPrimitive.Root
          ref={ref}
          name={name}
          required={required}
          checked={checked}
          disabled={disabled} // Temporary Case for Disabled Scopes
          defaultChecked={defaultChecked}
          className={cn(
            "peer h-5 w-5 shrink-0 mt-1 rounded-[2px] border-[1.5px] border-white/30 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-[#605BFF] data-[state=checked]:border-[#605BFF] data-[state=checked]:text-slate-50 transition-all duration-300",
            className
          )}
          onCheckedChange={(isChecked) => onChange(isChecked)}
        >
          <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
            <Icon name="check" className={"h-4 w-4"} />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>

        <label
          htmlFor={name}
          // Temporary Case for Disabled Scopes
          className={`font-light poppins-light ${
            !disabled ? "text-white/90" : "text-white/40"
          } leading-normal peer-disabled:cursor-not-allowed ml-2`}
        >
          {label}
        </label>
        {tooltip && disabled && (
          <div className="absolute left-8 bottom-full mb-2 hidden group-hover:block bg-slate-900 text-white text-sm font-medium rounded-lg py-2 px-3 shadow-lg whitespace-nowrap z-50 border border-slate-700">
            <div className="absolute left-4 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-slate-900"></div>
            {tooltip}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

const CheckboxGroup = forwardRef(
  (
    {
      className,
      options,
      onChange,
      name,
      defaultValue = [],
      value = [],
      ...props
    },
    ref
  ) => {
    const handleChange = (label, isChecked) => {
      const updatedValue = isChecked
        ? [...value, label] // Add checked item
        : value.filter((item) => item !== label); // Remove unchecked item

      onChange({ target: { name, value: updatedValue }, type: "change" });
    };

    const scopeMappings = {
      maps: "Maps API",
      autocomplete: "Autocomplete API",
      geocode: "Geocoding API",
      directions: "Directions API",
      map_matching: "Map Matching API",
      optimize: "Optimization API",
      matrix: "Matrix API",
      isochrone: "Isochrone API",
      elevation: "Elevation API",
      navigation: "Navigation SDK",
      tiles: "Tiles API",
      staticImages: "Static Images API",
      // weather: "Weather API",
    };

    // Temporary Case for Disabled Scopes
    const temporarilyDisallowedCase = [
      // "maps",
      // "autocomplete",
      // "geocoding",
      // "directions",
      "navigation",
      "tiles",
      "staticImages",
    ];

    // Temporary Case for Disabled Scopes
    const isTempDisallowedCase =
      JSON.stringify(options) === JSON.stringify(temporarilyDisallowedCase);

    return (
      <fieldset
        ref={ref}
        className={cn("flex flex-col items-start gap-2", className)}
        {...props}
      >
        {options?.length &&
          options.map((option) => {
            // Support both string options and object options { value, label, disabled, tooltip }
            const optionValue = typeof option === "object" ? option.value : option;
            const optionLabel = typeof option === "object" && option.label
              ? option.label
              : (scopeMappings[optionValue] ? scopeMappings[optionValue] : optionValue);
            const optionDisabled = typeof option === "object" && option.disabled
              ? option.disabled
              : (isTempDisallowedCase && optionValue == "geocoding") ||
                optionValue == "navigation" ||
                optionValue == "tiles" ||
                optionValue == "staticImages";
            const optionTooltip = typeof option === "object" ? option.tooltip : null;

            return (
              <Checkbox
                key={optionValue}
                label={optionLabel}
                name={name}
                disabled={optionDisabled}
                tooltip={optionTooltip}
                onChange={(isChecked) => handleChange(optionValue, isChecked)}
                checked={value.includes(optionValue)}
                defaultChecked={defaultValue.includes(optionValue)}
              />
            );
          })}
      </fieldset>
    );
  }
);

CheckboxGroup.displayName = "CheckboxGroup";

export { Checkbox, CheckboxGroup };
